import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

export interface IDepartamento {
  id: number;
  nome: string;
  status: string;
}

export interface IUsuarioDepartamento {
  id: number;
  nome: string;
  email: string;
  imagem_url?: string | null;
}

// Interface que representa a resposta da API (estrutura plana)
export interface IDepartamentoWithUserRaw {
  id: number;
  nome: string;
  status: string;
  user_id: number | null;
  user_name?: string;
  email: string | null;
  imagem_url?: string | null;
}

// Interface transformada para uso no frontend (usuários agrupados)
export interface IDepartamentoWithUsers {
  id: number;
  nome: string;
  status: string;
  usuarios: IUsuarioDepartamento[];
}

interface IDepartamentoFilter {
  departamentoId: string;
}

export const useDepartamentos = () => {
  const getDepartamentos = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IDepartamento[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IDepartamento[]>>('/api/v1/departamentos/', {
      params: filters,
    });
    return res.data;
  };

  const getDepartamento = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IDepartamento>> => {
    const { departamentoId } = key.queryKey[1] as IDepartamentoFilter;
    const res = await api.get<IDataNoPagination<IDepartamento>>(
      `/api/v1/departamentos/${departamentoId}/`,
    );
    return res.data;
  };

  const createDepartamento = async (
    data: Omit<IDepartamento, 'id'>,
  ): Promise<IDataNoPagination<IDepartamento>> => {
    const res = await api.post<IDataNoPagination<IDepartamento>>('/api/v1/departamentos/', data);
    return res.data;
  };

  const updateDepartamento = async (
    data: IDepartamento,
  ): Promise<IDataNoPagination<IDepartamento>> => {
    const res = await api.put<IDataNoPagination<IDepartamento>>(
      `/api/v1/departamentos/${data.id}/`,
      data,
    );
    return res.data;
  };

  const deleteDepartamento = async (departamentoId: string) => {
    const res = await api.delete(`/api/v1/departamentos/${departamentoId}/`);
    return res.data;
  };

  const getDepartamentosWithUsers = async (): Promise<
    IDataNoPagination<IDepartamentoWithUserRaw[]>
  > => {
    const res = await api.get<IDataNoPagination<IDepartamentoWithUserRaw[]>>(
      '/api/v1/departamentos/with-users/',
    );
    return res.data;
  };

  return {
    getDepartamentos,
    getDepartamento,
    createDepartamento,
    updateDepartamento,
    deleteDepartamento,
    getDepartamentosWithUsers,
  };
};
